import { logger } from '@fe/core';
import { GeoJSONSource, MapMouseEvent, PointLike, Subscription } from 'maplibre-gl';
import { GDFPalletCode } from '../../gdf/type';
import {
    WeatherPickingInfoLayer,
    WeatherPickingInfoLayerHoverClear,
    WeatherPickingInfoLayerHoverContext,
    WeatherPickingInfoObject
} from '../../manager/types';
import products from '../../products/products';
import { WeatherSettingsChangeSource } from '../../settings/settings';
import { MessageType } from '../../worker/actor/actor-messages';
import { changeSourceAffectsProduct } from '../change-source';
import { WeatherLayerBase } from '../weather-layer-base';
import { getStylingFactory, StyleFactory, WeatherLayerSpecification } from './styling';

const PICK_PADDING = 2;

export class GeoJsonLayer extends WeatherLayerBase {
    private styleFactory: StyleFactory | null = null;
    private layers: WeatherLayerSpecification[] = [];
    private pickableLayers: string[] = [];
    private hoverContext: WeatherPickingInfoLayerHoverContext = {
        setHover: this.hoverFeature.bind(this)
    };
    private paletteCode: GDFPalletCode = GDFPalletCode.none;
    private themeSub: Subscription | null = null;

    protected onDisplayChanged(): void {
        if (this.shouldDisplay()) {
            this.layers
                .filter((x) => x.layout?.visibility !== 'none')
                .forEach((layer) => this.map.setLayoutProperty(layer.id, 'visibility', 'visible'));
        } else {
            this.layers.forEach((layer) => this.map.setLayoutProperty(layer.id, 'visibility', 'none'));
        }
    }

    protected onSettingsUpdated(changeSource: WeatherSettingsChangeSource): void {
        // If not loaded yet
        if (!this.info || this.visibleFrame === null) {
            return;
        }

        const palette = this.info.frames[this.visibleFrame].header.productPallet;
        const reloadInfo = changeSourceAffectsProduct(this.productId, palette, changeSource);

        if (!reloadInfo.reloadLayers && !reloadInfo.reloadData) {
            return;
        }

        logger.debug(
            "Reloading layer '%s' because change in '%s' affects palette '%s' or product id: %d.",
            this.baseId,
            WeatherSettingsChangeSource[changeSource],
            GDFPalletCode[palette],
            this.productId
        );

        if (reloadInfo.reloadData) {
            if (this.visibleFrame !== null) {
                this.loadFrame(this.visibleFrame);
            }
        }

        if (reloadInfo.reloadLayers) {
            this.removeLayers();
            this.addLayers();
        }
    }

    protected async loadFrame(frameId: number) {
        const source = this.map.getSource(this.baseId) as GeoJSONSource | null;
        if (!source) {
            return;
        }

        // This can be cached in here, don't no need to request it every time.
        const data = await this.dispatcher.dispatch({
            type: MessageType.getDataGeoJson,
            data: { id: this.productId, frame: frameId, workspaceId: this.workspaceId }
        });

        this.paletteCode = data.paletteCode;

        if (!this.styleFactory) {
            this.addLayers();
        }

        source.updateData({
            removeAll: true,
            add: data.features.features
        });
    }

    protected onAdd(): void {
        this.map.addSource(this.baseId, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });

        this.themeSub = this.map.onThemeChanged(() => {
            if (this.layers.some((x) => x.themed)) {
                this.removeLayers();
                this.addLayers();
            }
        });
        // We can't add layers here because they depend on palette code which is not available until the product is loaded
    }

    protected onRemove(): void {
        this.themeSub?.unsubscribe();
        this.themeSub = null;

        this.removeLayers();

        if (this.map.getSource(this.baseId)) {
            this.map.removeSource(this.baseId);
        }
    }

    private addLayers() {
        this.styleFactory ??= getStylingFactory(this.productId, this.paletteCode);
        this.layers = this.styleFactory(this.baseId, this.settings, this.productId, this.map.isLightStyle ?? true);
        this.pickableLayers = this.layers.filter((x) => x.pickable).map((x) => x.id);
        this.layers.forEach((layer) => this.map.addLayer(layer, this.slotTopLayerId));

        // Call this to set the visibility of layers
        // This is due to the fact that all layers should start as hidden
        this.onDisplayChanged();
    }

    private removeLayers() {
        this.layers.forEach((layer) => this.map.removeLayer(layer.id));
    }

    private hoverFeature(record: WeatherPickingInfoObject): WeatherPickingInfoLayerHoverClear | null {
        if (record.type !== 'record' && record.type !== 'subRecord') {
            return null;
        }

        this.map.setFeatureState(
            {
                id: record.id,
                source: this.baseId
            },
            {
                hover: true
            }
        );
        return {
            clear: () => {
                this.map.removeFeatureState(
                    {
                        id: record.id,
                        source: this.baseId
                    },
                    'hover'
                );
            }
        };
    }

    async pickObjects(event: MapMouseEvent): Promise<WeatherPickingInfoLayer | null> {
        if (this.pickableLayers.length === 0) {
            return null;
        }

        const bounds: [PointLike, PointLike] = [
            [event.point.x - PICK_PADDING / 2, event.point.y - PICK_PADDING / 2],
            [event.point.x + PICK_PADDING / 2, event.point.y + PICK_PADDING / 2]
        ];

        const features = this.map.queryRenderedFeatures(bounds, { layers: this.pickableLayers });

        if (features.length === 0) {
            return null;
        }

        let sliceEnd: number | undefined = undefined;
        if (this.paletteCode === GDFPalletCode.metar || this.paletteCode === GDFPalletCode.taps) {
            sliceEnd = 1;
        }

        const items = await this.dispatcher.dispatch({
            type: MessageType.getTooltipData,
            data: {
                productId: this.productId,
                ids: features
                    .slice(0, sliceEnd)
                    .map((x) => x.id)
                    .filter((x) => x != null && typeof x === 'number'),
                frame: this.visibleFrame!
            }
        });

        if (!items || items.length === 0) {
            return null;
        }

        return {
            product: products[this.productId],
            hover: this.hoverContext,
            palette: this.paletteCode,
            objects: items
        };
    }
}

/*

cool idea: compare list of layers and apply the changes 

function compareAndApply(map: maplibregl.Map, oldLayers: WeatherLayerSpecification[], newLayers: WeatherLayerSpecification[]) {
    for (const newLayer of newLayers) {
        const oldLayer = oldLayers.find((x) => x.id === newLayer.id);
        if (!oldLayer) {
            logger.info("Layer '%s' was not part of the previous layer list", newLayer.id);
            continue;
        }

        if (oldLayer.paint && newLayer.paint) {
            const oldLayerPaint = Object.entries(oldLayer.paint);
            const newLayerPaint = Object.entries(newLayer.paint);

            for (const newLayerPaintProperty of newLayerPaint) {
                const oldLayerPaintProperty = oldLayerPaint.find((x) => x[0] === newLayerPaintProperty[0]);
                if (oldLayerPaintProperty) {
                    if (!compareExpressions(newLayerPaintProperty[1], oldLayerPaintProperty[1])) {
                        logger.info("Change detected in property '%s'", oldLayerPaintProperty[0]);
                        map.setPaintProperty(newLayer.id, newLayerPaintProperty[0], newLayerPaintProperty[1]);
                    } else {
                        // logger.info("NO change in property '%s'", oldLayerPaintProperty[0]);
                    }
                } else {
                    // new property
                    map.setLayoutProperty(newLayer.id, newLayerPaintProperty[0], newLayerPaintProperty[1]);
                }
            }
        }

        if (oldLayer.layout && newLayer.layout) {
            const oldLayerLayout = Object.entries(oldLayer.layout);
            const newLayerLayout = Object.entries(newLayer.layout);

            for (const newLayerLayoutProperty of newLayerLayout) {
                const oldLayerLayoutProperty = oldLayerLayout.find((x) => x[0] === newLayerLayoutProperty[0]);
                if (oldLayerLayoutProperty) {
                    if (!compareExpressions(oldLayerLayoutProperty[1], newLayerLayoutProperty[1])) {
                        logger.info("Change detected in property '%s'", oldLayerLayoutProperty[0]);
                        map.setLayoutProperty(newLayer.id, newLayerLayoutProperty[0], newLayerLayoutProperty[1]);
                    } else {
                        //logger.info("NO change in property '%s'", oldLayerLayoutProperty[0]);
                    }
                } else {
                    // new property
                    map.setLayoutProperty(newLayer.id, newLayerLayoutProperty[0], newLayerLayoutProperty[1]);
                }
            }
        }
    }
}

function compareExpressions(o1: any, o2: any): boolean {
    if (Array.isArray(o1) && Array.isArray(o2)) {
        if (o1.length !== o2.length) {
            return false;
        }

        for (let i = 0; i < o1.length; i++) {
            if (!compareExpressions(o1[i], o2[i])) {
                return false;
            }
        }
        return true;
    } else if (typeof o1 === 'object' && typeof o2 === 'object') {
        const k1 = Object.keys(o1);
        const k2 = Object.keys(o2);
        if (k1.length !== k2.length) {
            return false;
        }

        for (const k of k1) {
            if (!compareExpressions(o1[k], o2[k])) {
                return false;
            }
        }

        return true;
    }

    return o1 === o2;
}
*/
